import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getLogger } from '@/utils/logger';

const log = getLogger({ module: 'ContextMemory' });

interface SearchSession {
  id: string;
  userId: string;
  startedAt: Date;
  endedAt?: Date;
  queries: SearchQuery[];
  moodProgression: MoodTransition[];
  discoveryPath: string[];
  satisfaction?: number;
}

interface SearchQuery {
  query: string;
  timestamp: Date;
  results: number;
  engaged: boolean;
  mood?: string;
}

interface MoodTransition {
  from?: string;
  to: string;
  timestamp: Date;
  trigger?: string;
  confidence: number;
}

interface SerendipityTrigger {
  type: 'mood_shift' | 'discovery_pattern' | 'collaborative_signal';
  content: any[];
  reason: string;
  confidence: number;
}

export const useContextMemory = (userId?: string) => {
  const [currentSession, setCurrentSession] = useState<SearchSession | null>(null);
  const [moodHistory, setMoodHistory] = useState<MoodTransition[]>([]);
  const [serendipityTriggers, setSerendipityTriggers] = useState<SerendipityTrigger[]>([]);
  const sessionRef = useRef<string | null>(null);

  // Initialize or continue session
  const initializeSession = useCallback(async () => {
    if (!userId) return;

    try {
      // Check for recent active session (within last 30 minutes)
      const { data: recentSession } = await supabase
        .from('contextsearch_history')
        .select('*')
        .eq('user_id', userId)
        .gte('created_at', new Date(Date.now() - 30 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (recentSession) {
        // Continue existing session
        sessionRef.current = recentSession.id;
        log.info('Continuing existing session', { sessionId: recentSession.id });
      } else {
        // Create new session
        const newSessionId = crypto.randomUUID();
        sessionRef.current = newSessionId;
        
        const newSession: SearchSession = {
          id: newSessionId,
          userId,
          startedAt: new Date(),
          queries: [],
          moodProgression: [],
          discoveryPath: []
        };
        
        setCurrentSession(newSession);
        log.info('Started new session', { sessionId: newSessionId });
      }

      // Load mood history for context
      await loadMoodHistory();
      
    } catch (error) {
      log.error('Error initializing session', error);
    }
  }, [userId]);

  // Load user's mood history for pattern detection
  const loadMoodHistory = useCallback(async () => {
    if (!userId) return;

    try {
      const { data: history } = await supabase
        .from('contextsearch_history')
        .select('user_mood_at_search, created_at, original_query, search_satisfaction_score')
        .eq('user_id', userId)
        .not('user_mood_at_search', 'is', null)
        .order('created_at', { ascending: false })
        .limit(50);

      if (history) {
        const transitions: MoodTransition[] = [];
        
        for (let i = 1; i < history.length; i++) {
          const current = history[i - 1];
          const previous = history[i];
          
          if (current.user_mood_at_search !== previous.user_mood_at_search) {
            transitions.push({
              from: previous.user_mood_at_search,
              to: current.user_mood_at_search,
              timestamp: new Date(current.created_at),
              confidence: 0.8
            });
          }
        }
        
        setMoodHistory(transitions);
        log.info('Loaded mood history', { transitionCount: transitions.length });
      }
    } catch (error) {
      log.error('Error loading mood history', error);
    }
  }, [userId]);

  // Track search query with context
  const trackSearchQuery = useCallback(async (
    query: string,
    resultsCount: number,
    detectedMood?: string
  ) => {
    if (!userId || !sessionRef.current) return;

    try {
      const searchQuery: SearchQuery = {
        query,
        timestamp: new Date(),
        results: resultsCount,
        engaged: false, // Will be updated on interaction
        mood: detectedMood
      };

      // Detect mood transitions
      const lastMood = moodHistory[0]?.to;
      if (detectedMood && lastMood && detectedMood !== lastMood) {
        const transition: MoodTransition = {
          from: lastMood,
          to: detectedMood,
          timestamp: new Date(),
          trigger: 'search_query',
          confidence: 0.7
        };
        
        setMoodHistory(prev => [transition, ...prev.slice(0, 19)]);
        
        // Check for serendipity opportunities
        await checkSerendipityTriggers(transition);
      }

      // Save to database
      await supabase
        .from('contextsearch_history')
        .insert({
          user_id: userId,
          original_query: query,
          parsed_intent: { session_id: sessionRef.current },
          results_returned: resultsCount,
          user_mood_at_search: detectedMood,
          time_of_day: new Date().getHours(),
          day_of_week: new Date().getDay()
        });

      log.info('Tracked search query', { query, mood: detectedMood, sessionId: sessionRef.current });
      
    } catch (error) {
      log.error('Error tracking search query', error);
    }
  }, [userId, moodHistory]);

  // Track user interaction with content
  const trackContentInteraction = useCallback(async (
    contentId: string,
    action: 'click' | 'like' | 'share' | 'long_view'
  ) => {
    if (!userId || !sessionRef.current) return;

    try {
      // Update search history with engagement
      const { data: recentSearch } = await supabase
        .from('contextsearch_history')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (recentSearch) {
        const updates: any = {};
        
        switch (action) {
          case 'click':
            updates.posts_clicked = (recentSearch.posts_clicked || 0) + 1;
            break;
          case 'like':
            updates.posts_liked = (recentSearch.posts_liked || 0) + 1;
            break;
          case 'share':
            updates.posts_shared = (recentSearch.posts_shared || 0) + 1;
            break;
        }

        await supabase
          .from('contextsearch_history')
          .update(updates)
          .eq('id', recentSearch.id);
      }

      log.info('Tracked content interaction', { contentId, action, sessionId: sessionRef.current });
      
    } catch (error) {
      log.error('Error tracking content interaction', error);
    }
  }, [userId]);

  // Check for serendipity triggers based on mood transitions
  const checkSerendipityTriggers = useCallback(async (transition: MoodTransition) => {
    try {
      // Pattern: Sad -> Happy (inspirational content)
      if (transition.from === 'sad' && transition.to === 'happy') {
        const trigger: SerendipityTrigger = {
          type: 'mood_shift',
          content: [], // Would fetch inspirational content
          reason: 'Detected mood improvement - suggesting uplifting content',
          confidence: 0.8
        };
        
        setSerendipityTriggers(prev => [trigger, ...prev.slice(0, 4)]);
      }
      
      // Pattern: Bored -> Creative (creative challenges)
      else if (transition.from === 'calm' && transition.to === 'creative') {
        const trigger: SerendipityTrigger = {
          type: 'mood_shift',
          content: [], // Would fetch creative prompts
          reason: 'Creative mood detected - suggesting artistic inspiration',
          confidence: 0.7
        };
        
        setSerendipityTriggers(prev => [trigger, ...prev.slice(0, 4)]);
      }

      log.info('Serendipity trigger evaluated', { 
        from: transition.from, 
        to: transition.to,
        triggered: true 
      });
      
    } catch (error) {
      log.error('Error checking serendipity triggers', error);
    }
  }, []);

  // Get context for recommendations
  const getContextualRecommendations = useCallback(async (currentQuery: string) => {
    if (!userId) return [];

    try {
      // Get similar successful discovery paths
      const { data: similarPaths } = await supabase
        .from('contextsearch_history')
        .select('original_query, posts_clicked, posts_liked, search_satisfaction_score')
        .eq('user_id', userId)
        .gt('search_satisfaction_score', 0.7)
        .gt('posts_clicked', 0)
        .limit(10);

      return similarPaths || [];
      
    } catch (error) {
      log.error('Error getting contextual recommendations', error);
      return [];
    }
  }, [userId]);

  // End current session
  const endSession = useCallback(async (satisfaction?: number) => {
    if (!userId || !sessionRef.current) return;

    try {
      // Update the most recent search with satisfaction score
      if (satisfaction) {
        const { data: recentSearch } = await supabase
          .from('contextsearch_history')
          .select('id')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (recentSearch) {
          await supabase
            .from('contextsearch_history')
            .update({ search_satisfaction_score: satisfaction })
            .eq('id', recentSearch.id);
        }
      }

      log.info('Session ended', { sessionId: sessionRef.current, satisfaction });
      sessionRef.current = null;
      setCurrentSession(null);
      
    } catch (error) {
      log.error('Error ending session', error);
    }
  }, [userId]);

  useEffect(() => {
    initializeSession();
    
    // End session on page unload
    const handleUnload = () => {
      endSession();
    };
    
    window.addEventListener('beforeunload', handleUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      endSession();
    };
  }, [initializeSession, endSession]);

  return {
    currentSession,
    moodHistory,
    serendipityTriggers,
    trackSearchQuery,
    trackContentInteraction,
    getContextualRecommendations,
    endSession
  };
};